import { AlertTriangle, Check, Loader2, X } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import type { EvalProposal } from "@/api/evals";

const CHANGE_LABELS: Record<EvalProposal["changeType"], string> = {
  workflow_publish: "Publish workflow",
  workflow_node_model: "Change step model",
  workflow_node_prompt: "Change step prompt",
};

/**
 * A change the Auditor has drafted, shown as a before/after with an explicit
 * Confirm.
 *
 * The diff is the point. The agent proposes; the person decides; nothing is
 * written until this button is pressed. So the card has to show enough to
 * decide on — what changes, from what to what, and the argument for it — not
 * just a summary and a button, which would make confirming a formality.
 */
export function ProposalCard({
  proposal,
  onDecide,
}: {
  proposal: EvalProposal;
  onDecide: (id: string, decision: "apply" | "dismiss") => Promise<void>;
}) {
  const [busy, setBusy] = useState<"apply" | "dismiss" | null>(null);

  const decide = async (decision: "apply" | "dismiss") => {
    setBusy(decision);
    try {
      await onDecide(proposal.id, decision);
    } finally {
      setBusy(null);
    }
  };

  const pending = proposal.status === "pending";
  const before = describeValue(proposal.changeType, proposal.before, "before");
  const after = describeValue(proposal.changeType, proposal.after, "after");

  return (
    <div
      className={cn(
        "rounded-lg border px-3 py-2.5 text-[12.5px]",
        pending ? "border-accent/40 bg-accent/5" : "border-border bg-surface/60",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-text-primary">
            {CHANGE_LABELS[proposal.changeType] ?? proposal.changeType}
          </div>
          {proposal.targetName && (
            <div className="truncate text-[11.5px] text-text-muted">{proposal.targetName}</div>
          )}
        </div>
        <StatusPill status={proposal.status} />
      </div>

      <div className="mt-2 space-y-1">
        <DiffRow label="Now" value={before} tone="before" />
        <DiffRow label="Proposed" value={after} tone="after" />
      </div>

      {proposal.rationale && (
        <p className="mt-2 text-[11.5px] leading-relaxed text-text-secondary">
          {proposal.rationale}
        </p>
      )}

      {/* A stale proposal explains itself — the target moved after it was
          drafted, so the diff above no longer describes reality. */}
      {proposal.statusDetail && proposal.status !== "applied" && (
        <p className="mt-2 flex items-start gap-1.5 text-[11.5px] text-amber-600 dark:text-amber-500">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          {proposal.statusDetail}
        </p>
      )}

      {pending && (
        <div className="mt-2.5 flex gap-2">
          <button
            onClick={() => decide("apply")}
            disabled={busy !== null}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent px-2.5 py-1 text-[12px] font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {busy === "apply" ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
            Confirm
          </button>
          <button
            onClick={() => decide("dismiss")}
            disabled={busy !== null}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-[12px] text-text-secondary hover:bg-surface-hover disabled:opacity-50"
          >
            <X size={13} />
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}

function DiffRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "before" | "after";
}) {
  return (
    <div className="flex gap-2">
      <span className="w-[62px] shrink-0 text-[11px] uppercase tracking-wide text-text-muted">
        {label}
      </span>
      <span
        className={cn(
          "min-w-0 flex-1 whitespace-pre-wrap break-words rounded px-1.5 py-0.5 font-mono text-[11.5px]",
          tone === "before"
            ? "bg-rose-500/10 text-rose-700 dark:text-rose-300"
            : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
        )}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * Render the side of the diff a reviewer needs to see. Values are capped —
 * a full replacement prompt can run to thousands of characters, and a card
 * nobody reads to the end is a card that gets confirmed unread.
 */
function describeValue(
  changeType: EvalProposal["changeType"],
  value: Record<string, unknown> | null,
  side: "before" | "after",
): string {
  if (changeType === "workflow_publish") {
    return side === "before" ? String(value?.status ?? "draft") : "active";
  }
  if (changeType === "workflow_node_model") {
    const model = value?.model;
    if (model === null || model === undefined || model === "") {
      return "(agent's own model)";
    }
    return String(model);
  }
  const prompt = value?.prompt;
  if (prompt === null || prompt === undefined || prompt === "") return "(empty)";
  const text = String(prompt);
  return text.length > 600 ? `${text.slice(0, 600)}…` : text;
}

function StatusPill({ status }: { status: EvalProposal["status"] }) {
  const styles: Record<string, string> = {
    pending: "border-accent/40 text-accent",
    applied: "border-emerald-500/40 text-emerald-600 dark:text-emerald-400",
    dismissed: "border-border text-text-muted",
    stale: "border-amber-500/40 text-amber-600 dark:text-amber-500",
    failed: "border-rose-500/40 text-rose-600 dark:text-rose-400",
  };
  return (
    <span
      className={cn(
        "shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium",
        styles[status] ?? styles.dismissed,
      )}
    >
      {status}
    </span>
  );
}
